import { queryClient } from 'api/QueryClient';
import { updateBeeEditorCustomFonts } from 'api/TemplateEditAPI';
import { CustomFont } from 'modules/templateEditor/context/ContentEditorPreferencesContext';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { GET_BEE_CUSTOM_FONTS_CACHE_KEY, useQueryBeeCustomFonts } from '../useQueryBeeCustomFonts';
import {
  generateCreateFontPayload,
  generateDeleteFontPayload,
  generateUpdateFontPayload,
} from './useManageBeeCustomFonts.utils';

/**
 * Exposes API for performing CRUD operations on bee custom fonts.
 *
 * NOTE: The db column bee_editor_fonts is "string" inside of the projects table that holds all of the custom fonts.
 * so, by performing CRUD on a font, we need to actually send an array with all of the fonts containing the edit in place of the original.
 * consequently: duplicating the way the angular version works: this saga first fetches all of the existing custom fonts
 * and then sends an array with the updated fonts list.
 */
export const useManageBeeCustomFonts = () => {
  const dispatch = useDispatch();

  const {
    customFonts,
    error: customFontsError,
    isLoading: isFetchingCustomFonts,
    refetch: getCustomFonts,
  } = useQueryBeeCustomFonts({
    onError(error) {
      dispatch(
        addNegativeToastWithErrorId(
          'Unable to fetch custom fonts',
          'useManageBeeCustomFonts',
          error,
        ),
      );
    },
  });

  const addCustomFont = useCallback(
    (customFont: CustomFont) => {
      getCustomFonts()
        .then(({ data }) => {
          return generateCreateFontPayload(data?.fonts, customFont);
        })
        .then(async (fonts) => {
          await updateBeeEditorCustomFonts(fonts);
          queryClient.setQueryData(GET_BEE_CUSTOM_FONTS_CACHE_KEY, { fonts });
        })
        .then(() => {
          dispatch(addPositiveToast('Successfully updated custom fonts'));
        })
        .catch((error: any) => {
          dispatch(
            addNegativeToastWithErrorId(
              'Unable to add custom font',
              'useManageBeeCustomFonts',
              error,
            ),
          );
        });
    },
    [getCustomFonts, dispatch],
  );

  const deleteCustomFont = useCallback(
    (fontName: string) => {
      getCustomFonts()
        .then(({ data }) => {
          return generateDeleteFontPayload(data?.fonts, fontName);
        })
        .then(async (fonts) => {
          await updateBeeEditorCustomFonts(fonts);
          queryClient.setQueryData(GET_BEE_CUSTOM_FONTS_CACHE_KEY, { fonts });
        })
        .then(() => {
          dispatch(addPositiveToast('Successfully deleted custom fonts'));
        })
        .catch((error: any) => {
          dispatch(
            addNegativeToastWithErrorId(
              'Unable to delete custom font',
              'useManageBeeCustomFonts',
              error,
            ),
          );
        });
    },
    [getCustomFonts, dispatch],
  );

  const editCustomFont = useCallback(
    (originalFontName: string, updatedFont: CustomFont) => {
      getCustomFonts()
        .then(({ data }) => {
          return generateUpdateFontPayload(data?.fonts, originalFontName, updatedFont);
        })
        .then(async (fonts) => {
          await updateBeeEditorCustomFonts(fonts);
          queryClient.setQueryData(GET_BEE_CUSTOM_FONTS_CACHE_KEY, { fonts });
        })
        .then(() => {
          dispatch(addPositiveToast('Successfully updated custom fonts'));
        })
        .catch((error: any) => {
          dispatch(
            addNegativeToastWithErrorId(
              'Unable to edit custom font',
              'useManageBeeCustomFonts',
              error,
            ),
          );
        });
    },
    [getCustomFonts, dispatch],
  );

  return {
    customFonts,
    customFontsError,
    isFetchingCustomFonts,
    getCustomFonts,
    addCustomFont,
    editCustomFont,
    deleteCustomFont,
  };
};
